import './Modal.css';

import { Fab, Text, concatStringish } from '@bendhealth/common';
import React, { FC, HTMLAttributes, ReactNode, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { X } from 'react-feather';

export interface ModalProps {
  canExit?: boolean;
  onExit?: () => void;
  children: ReactNode;
  titleText?: string;
  headerNode?: ReactNode;
  titleClassName?: string;
  open?: boolean;
  withOverlay?: boolean;
  draggable?: boolean;
  minimized?: boolean;
  player?: boolean;
  className?: string;
}

type ModalComponent = FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

interface ModalHeaderActionsProps extends HTMLAttributes<HTMLDivElement> {
  showEscape?: boolean;
  handleEscape?: () => void;
}

const ModalHeaderActions: FC<ModalHeaderActionsProps> = ({ className, children, showEscape, handleEscape }) => (
  <div className={concatStringish('ModalHeaderActions', className)}>
    {children}

    {showEscape && handleEscape && (
      <Fab aria-label="close button" onClick={handleEscape} variants={['small']}>
        <X size={20} />
      </Fab>
    )}
  </div>
);

export const ModalHeader: FC<
  HTMLAttributes<HTMLDivElement> & {
    showEscape?: boolean;
    titleText?: string;
    handleEscape?: () => void;
    actions?: ReactNode;
  }
> = ({ showEscape, className, children, titleText, handleEscape, actions, ...rest }) => (
  <header {...rest} className={concatStringish('ModalHeader', className)}>
    <div className="ModalHeader__Content">
      {titleText && <Text variant="body-2">{titleText}</Text>}
      {children}
    </div>

    <ModalHeaderActions showEscape={showEscape} handleEscape={handleEscape}>
      {actions}
    </ModalHeaderActions>
  </header>
);

export const ModalBody: FC<HTMLAttributes<HTMLDivElement>> = ({ className, children, ...rest }) => (
  <section {...rest} className={concatStringish('ModalBody', className)}>
    {children}
  </section>
);

export const ModalFooter: FC<HTMLAttributes<HTMLDivElement>> = ({ className, children, ...rest }) => (
  <footer {...rest} className={concatStringish('ModalFooter', className)}>
    {children}
  </footer>
);

const getDraggableBounds = (draggableRef: RefObject<HTMLDivElement>, minimized: boolean) => {
  if (!draggableRef.current) {
    return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  }

  const computedStyle = getComputedStyle(draggableRef.current);

  if (minimized) {
    return {
      top: -draggableRef.current.offsetTop,
      left: -parseInt(computedStyle.left),
      right: window.innerWidth - draggableRef.current.clientWidth - parseInt(computedStyle.left),
      bottom: parseInt(computedStyle.bottom),
    };
  }

  return {
    top: -draggableRef.current.offsetTop,
    left: -parseInt(computedStyle.marginLeft),
    right: parseInt(computedStyle.marginRight),
    bottom: draggableRef.current.offsetTop,
  };
};

export const Modal: ModalComponent = ({
  canExit = true,
  children,
  onExit,
  open = false,
  withOverlay = true,
  draggable = false,
  minimized = false,
  player = false,
  className,
}: ModalProps) => {
  const handleEscape = useCallback(() => {
    onExit?.();
  }, [onExit]);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const draggableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && canExit && handleEscape();

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [handleEscape, canExit]);

  useEffect(() => {
    setModalPosition({ x: 0, y: 0 });
  }, [minimized, open]);

  const draggableBounds = getDraggableBounds(draggableRef, minimized);

  const hasCustomHeader = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === ModalHeader,
  );
  const children_with_draggable_header = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === ModalHeader) {
      return React.cloneElement(child as React.ReactElement<HTMLAttributes<HTMLDivElement>>, {
        className: concatStringish(child.props.className, 'ModalHeader--draggable'),
      });
    }
    return child;
  });

  return createPortal(
    <div
      className={`ModalContainer
        ${open ? 'ModalContainer--open' : 'ModalContainer--closed'}
        ${minimized ? 'ModalContainer--minimized' : ''}
        ${draggable ? 'ModalContainer--draggable' : ''}
        ${player ? 'ModalContainer--player' : ''}
      `}
    >
      {withOverlay && <div className="ModalContainer__Overlay" onClick={() => canExit && handleEscape()}></div>}
      {draggable && (
        <Draggable
          handle=".ModalHeader--draggable"
          position={modalPosition}
          bounds={draggableBounds}
          onDrag={(e, data) => setModalPosition({ x: data.x, y: data.y })}
        >
          <div className={`Modal ${withOverlay ? 'Modal--withOverlay' : 'Modal--withoutOverlay'}`} ref={draggableRef}>
            {!hasCustomHeader && (
              <ModalHeader showEscape handleEscape={handleEscape} className="ModalHeader--draggable" />
            )}
            {children_with_draggable_header}
          </div>
        </Draggable>
      )}
      {!draggable && (
        <div className={`Modal ${withOverlay ? 'Modal--withOverlay' : 'Modal--withoutOverlay'}`}>{children}</div>
      )}
    </div>,
    window.document.body,
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
